import { Typography } from '@mui/material';
import { createSelector } from '@reduxjs/toolkit';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { ComplementaryConsolidation } from '../../api/complementaryConsolidation';
import Description from '../../components/Description/Description';
import { ErrorMessage } from '../../components/ErrorMessage';
import PagesFooter from '../../components/PagesFooter/PagesFooter';
import PagesHeader from '../../components/PagesHeader/PagesHeader';
import ShowDetailsModal from '../../components/ShowDetailsModal/ShowDetailsModal';
import Title from '../../components/Title/Title';
import { usePagesContext } from '../../context/PagesContext';
import { scrollToFieldWithError } from '../../helpers/scrollToFieldWithError';
import { useAppDispatch, useAppSelector } from '../../hooks';
import useArnFromPath from '../../hooks/useArnFromPath';
import useComplementaryConsolidationService from '../../hooks/useComplementaryConsolidationService';
import useComponentTracker from '../../hooks/useComponentTracker';
import useDefaultPagesAction from '../../hooks/useDefaultPagesAction';
import useGetSelectedConsolidations from '../../hooks/useGetSelectedConsolidations';
import { usePageFocus } from '../../hooks/usePageFocusHook';
import usePagesConfig from '../../hooks/usePagesConfig';
import useSetBannerType from '../../hooks/useSetBannerType';
import useTranslation from '../../hooks/useTranslation';
import useValidation from '../../hooks/useValidation';
import { RootState } from '../../store';
import { getApplicationState } from '../../store/application/application';
import { addOrUpdateFormField } from '../../store/applicationform';
import { IFieldState } from '../../store/applicationform/applicationFormTypes';
import { WizardPageAction } from '../../store/wizard/wizard';
import { Consolidation } from '../../types/ConsolidationTypes';
import ConsolidationLoanCard from './ConsolidationLoanCard';

export const selectAllConsolidationFields: (state: RootState) => () => IFieldState[] = createSelector(
    (state: RootState) => state,
    (state: RootState) => (): IFieldState[] =>
        state.form.formFieldStates.filter((fieldState) => fieldState.fieldName.startsWith('consolidation')),
);

const ConsolidationLoanCards = ({
    consolidations,
    savedLoanIds,
    onSave,
    onUnsave,
    finalComplementaryConsolidations,
    setSaveError,
}: {
    consolidations: Consolidation[];
    savedLoanIds: Set<string>;
    onSave: (internalId: string) => Promise<void>;
    onUnsave: (internalId: string) => void;
    finalComplementaryConsolidations: ComplementaryConsolidation[];
    setSaveError: (hasError: boolean) => void;
}) => (
    <>
        {consolidations.map((consolidation) => (
            <ConsolidationLoanCard
                key={consolidation.internalId}
                consolidation={consolidation}
                isSaved={savedLoanIds.has(consolidation.internalId)}
                onSave={onSave}
                onUnsave={onUnsave}
                finalComplementaryConsolidations={finalComplementaryConsolidations}
                setSaveError={setSaveError}
            />
        ))}
    </>
);

const ConsolidationComplementInfoPage = () => {
    useComponentTracker('ConsolidationComplementInfoPage');
    useSetBannerType('bannerThree');
    usePageFocus();
    const { currentPage } = usePagesContext();
    const defaultPagesAction = useDefaultPagesAction();
    const { totalSections } = usePagesConfig();
    const dispatch = useAppDispatch();
    const translate = useTranslation();
    const [isError, setIsError] = useState(false);
    const { selectedConsolidations } = useGetSelectedConsolidations();
    const [initialComplementaryConsolidations, setInitialComplementaryConsolidations] = useState<
        ComplementaryConsolidation[]
    >([]);
    const [savedLoanIds, setSavedLoanIds] = useState<Set<string>>(new Set());
    const complementaryConsolidationService = useComplementaryConsolidationService();
    const consolidationFieldStates = useAppSelector(selectAllConsolidationFields)();
    const [saveError, setSaveError] = useState(false);

    const requiredConsolidations = selectedConsolidations?.consolidations?.reduce((field, debt) => {
        const kidNumberField = consolidationFieldStates.find(
            (field) => field.fieldName === `consolidation_${debt.internalId};kidNumber`,
        );
        if (kidNumberField?.hasError) {
            field.push(`consolidation_${debt.internalId};kidNumber`);
        }

        field.push(`consolidation_${debt.internalId};accountNumber`);

        if (!debt.creditorName) {
            field.push(`consolidation_${debt.internalId};creditorName`);
        }

        return field;
    }, [] as string[]);

    const validationErrors = useValidation(requiredConsolidations ?? []);
    const applicationReferenceNumber = useArnFromPath();

    useEffect(() => {
        const initialState = selectedConsolidations?.consolidations?.map((consolidation) => ({
            internalId: consolidation.internalId,
            creditorName: consolidation.creditorName || '',
            accountNumber: consolidation.accountNumber || '',
            kidNumber: consolidation.kidNumber || '',
        }));
        setInitialComplementaryConsolidations(initialState ?? []);

        const prefilledIds = new Set<string>();
        selectedConsolidations?.consolidations?.forEach((consolidation) => {
            if (consolidation.accountNumber) {
                prefilledIds.add(consolidation.internalId);
            }
        });
        if (prefilledIds.size > 0) {
            setSavedLoanIds(prefilledIds);
        }
    }, [selectedConsolidations]);

    const finalComplementaryConsolidations = useMemo(() => {
        if (!initialComplementaryConsolidations.length || !consolidationFieldStates.length) return [];

        const merged = initialComplementaryConsolidations.map((item) => {
            const initialItem: any = { ...item };

            consolidationFieldStates.forEach(({ fieldName, value }) => {
                const [prefix, key] = fieldName.split(';');
                const id = prefix.split('_')[1];

                if (item.internalId === id) {
                    initialItem[key] = value;
                }
            });

            return initialItem;
        });

        return merged;
    }, [initialComplementaryConsolidations, consolidationFieldStates]);

    const handleSaveLoan = useCallback(
        async (internalId: string) => {
            const loanData = finalComplementaryConsolidations.find((c) => c.internalId === internalId);
            if (!loanData) return;

            await complementaryConsolidationService([loanData]);
            setSavedLoanIds((prev) => new Set(prev).add(internalId));
        },
        [finalComplementaryConsolidations, complementaryConsolidationService],
    );

    const handleUnsaveLoan = useCallback((internalId: string) => {
        setSavedLoanIds((prev) => {
            const next = new Set(prev);
            next.delete(internalId);
            return next;
        });
    }, []);

    const onAction = async (action: WizardPageAction) => {
        if (action !== 'next') return true;

        if (validationErrors.length) {
            scrollToFieldWithError(validationErrors);
            validationErrors.forEach((validationError) => {
                dispatch(addOrUpdateFormField(validationError));
            });
            return false;
        }

        try {
            setIsError(false);
            await complementaryConsolidationService(finalComplementaryConsolidations);
            await dispatch(getApplicationState({ applicationReferenceNumber }));
            return true;
        } catch (error) {
            setIsError(true);
            throw error;
        }
    };

    const withCoApplicantsLoans = selectedConsolidations?.consolidations.some(
        (consolidation) => consolidation.applicantType === 'CO',
    );

    const noApplicantType = selectedConsolidations?.consolidations.some(
        (consolidation) => !consolidation.applicantType,
    );

    const cardListProps = {
        savedLoanIds,
        onSave: handleSaveLoan,
        onUnsave: handleUnsaveLoan,
        finalComplementaryConsolidations,
        setSaveError,
    };

    return (
        <>
            <PagesHeader
                actions={[]}
                onAction={defaultPagesAction}
                currentPage={currentPage}
                currentSection={3}
                numberOfSections={totalSections}
                numberOfPagesInSection={4}
                currentPageInSection={2}
                sectionTitle={translate('field.section.your-account')}
            />
            <Title text={translate('page.consolidation-accounts-information.title')} />
            <Description text={translate('page.consolidation-accounts-information.description')} />
            <ShowDetailsModal
                label={translate('dialog.consolidation-accounts-information.label')}
                dialogTitle={translate('dialog.consolidation-accounts-information.label')}
                dialogContent={translate('dialog.consolidation-accounts-information.content')}
            />
            {noApplicantType ? (
                <ConsolidationLoanCards
                    consolidations={selectedConsolidations?.consolidations ?? []}
                    {...cardListProps}
                />
            ) : withCoApplicantsLoans ? (
                <>
                    <Typography variant="subtitle1" pb={'12px'}>
                        {translate('page.consolidation-accounts-information.applicant-type.applicant')}
                    </Typography>
                    <ConsolidationLoanCards
                        consolidations={
                            selectedConsolidations?.consolidations.filter(
                                (consolidation) => consolidation.applicantType === 'MAIN',
                            ) ?? []
                        }
                        {...cardListProps}
                    />
                    <Typography variant="subtitle1" pb={'12px'}>
                        {translate('page.consolidation-accounts-information.applicant-type.co-applicant')}
                    </Typography>
                    <ConsolidationLoanCards
                        consolidations={
                            selectedConsolidations?.consolidations.filter(
                                (consolidation) => consolidation.applicantType === 'CO',
                            ) ?? []
                        }
                        {...cardListProps}
                    />
                </>
            ) : (
                <ConsolidationLoanCards
                    consolidations={selectedConsolidations?.consolidations ?? []}
                    {...cardListProps}
                />
            )}
            {(isError || saveError) && (
                <ErrorMessage title={translate('page.error.title')} description={translate('page.error.description')} />
            )}
            <PagesFooter
                actions={['next']}
                onAction={onAction}
                currentSection={3}
                numberOfSections={totalSections}
                numberOfPagesInSection={4}
                currentPageInSection={2}
                sectionTitle={translate('field.section.your-account')}
            />
        </>
    );
};

export default ConsolidationComplementInfoPage;
